<script>
export default {
  name: "DidoDatasetCard",
};
</script>

<script setup>
import { ref, watch, computed, inject } from "vue";
import api from "../api";
import { displayDateFormatDDMMYYYY, pause } from "../utils";
import DidoTextTruncate from "../components/commons/Truncate.vue";
import DidoChipsList from "../components/commons/chips/List.vue";

// import topics logos
import logoEnvironnement from "../assets/theme-environnement.png";
import logoLogement from "../assets/theme-logement.png";
import logoEnergie from "../assets/theme-energie.png";
import logoTransports from "../assets/theme-transport.png";
import logoClimat from "../assets/theme-climat.png";

// inject variable to know if we are in catalog
const inCatalog = inject("inCatalog", null);

// inject method to create a catalogUrl
const catalogUrl = inject("catalogUrl", () => null);

// define Props
const props = defineProps({
  apiUrl: { type: String, required: true },
  datasetId: { type: String, required: false, default: "" },
  dataset: { type: Object, required: false, default: null },
});

// define events emitted
const emit = defineEmits([
  "dataset-retrieved",
  "dataset-not-retrieved",
  "label-selection",
  "click-card",
  "bad-configuration",
]);

// define Reactive Data
const status = ref("");
const loading = ref(false);
const datasetObject = ref(null);
const tags = ref([]);
const zones = ref([]);

// define watchers
watch(
  () => [props.apiUrl, props.datasetId, props.dataset],
  () => getDataset(),
);

// create a computed flag to mark the widget as configured
const configured = computed(() => {
  if (!props.apiUrl) return false;
  if (!props.datasetId && !props.dataset) return false;
  return true;
});

// computed data for the specs of QCard
const tagQCard = computed(() => (inCatalog ? "a" : "div"));
const hrefQCard = computed(() =>
  inCatalog
    ? catalogUrl.value({ type: "dataset", datasetId: datasetObject.value.id })
    : null,
);
const titleQCard = computed(() =>
  inCatalog
    ? `Voir le détail du jeu de données ${datasetObject.value.title}`
    : null,
);
const hasClickEventQCard = computed(() => (inCatalog ? "click" : null));
const clickQCard = computed(
  () => () => emit("click-card", { datasetId: datasetObject.value.id }),
);

// computed data for modification date
const modificationDate = computed(() => {
  const dateToDisplay =
    datasetObject.value.last_modified || datasetObject.value.created_at;
  return displayDateFormatDDMMYYYY(dateToDisplay);
});
// computed data for the topic logo
const logo = computed(() => {
  if (datasetObject.value.topic === "Environnement") return logoEnvironnement;
  if (datasetObject.value.topic === "Logement") return logoLogement;
  if (datasetObject.value.topic === "Énergie") return logoEnergie;
  if (datasetObject.value.topic === "Transports") return logoTransports;
  if (datasetObject.value.topic === "Changement climatique") return logoClimat;
  return "";
});
// computed data for datafiles text
const datafilesText = computed(() => {
  if (!datasetObject.value.datafiles) return "";
  if (datasetObject.value.datafiles.length === 1) return "1 Fichier de données";
  if (datasetObject.value.datafiles.length > 1)
    return `${datasetObject.value.datafiles.length} Fichiers de données`;
  return "";
});
// computed data for attachments text
const attachmentsText = computed(() => {
  if (!datasetObject.value.attachments) return "";
  if (datasetObject.value.attachments.length === 1) return "1 Fichier Annexe";
  if (datasetObject.value.attachments.length > 1)
    return `${datasetObject.value.attachments.length} Fichiers Annexes`;
  return "";
});
// computed data for consultations counter text
const consultationsCounterText = computed(() => {
  if (!datasetObject.value.views) return "";
  else
    return `Nombre de consultations : ${datasetObject.value.views.toLocaleString()}`;
});

// a method to get dataset from props or api
const getDataset = async () => {
  // if widget bad configured
  if (!configured.value) {
    status.value = "bad-configured";
    loading.value = false;
    datasetObject.value = null;
    emit("bad-configuration");
    return;
  }
  // if dataset data is available , setting it in reactive datas (no api call required !!)
  if (props.dataset) {
    datasetObject.value = props.dataset;
    status.value = "available";
  }
  // if dataset id is set, api call for retrieving dataset and setting it in reactive datas
  if (props.datasetId) {
    loading.value = true;
    await Promise.all([
      api.getDataset(props.apiUrl, props.datasetId),
      pause(500),
    ])
      .then(([apiResult]) => {
        datasetObject.value = apiResult.dataset;
        status.value = apiResult.status;
        if (status.value !== "available")
          emit("dataset-not-retrieved", { datasetId: props.datasetId });
      })
      .finally(() => (loading.value = false));
    if (!datasetObject.value) return;

    emit("dataset-retrieved", { datasetId: datasetObject.value.id });
  }

  // get detail of zones
  zones.value = await Promise.all(
    datasetObject.value.spatial.zones.map(async (id) =>
      api
        .getGeozone(props.apiUrl, id)
        .then((apiZone) => ({ id, display: apiZone.data.NAME }))
        .then((zone) => {
          let display = zone.display;
          if (zone.display === "France") display = "France entière";
          if (zone.display === "Metropolitan France")
            display = "France métropolitaine";
          return { id: zone.id, display };
        })
        .catch(() => id),
    ),
  );

  // get detail of tags
  tags.value = await Promise.all(
    datasetObject.value.tags.map(async (id) =>
      api
        .getTag(props.apiUrl, id)
        .then((apiTag) => ({ id, display: apiTag.data.DISPLAY }))
        .catch(() => id),
    ),
  );
};

// Initialization: retrieve Dataset
getDataset();
</script>

<template>
  <div class="dido-widget-dataset-card relative-position">
    <!-- Loading is finished -->
    <template v-if="!loading">
      <!-- Bad configuration of widget -->
      <div
        v-if="status === 'bad-configured'"
        class="q-pa-sm row-start-center border-secondary text-black"
      >
        <div class="col-12">Impossible de charger le composant...</div>
        <div class="col-12">Les attributs suivant sont requis:</div>
        <ul class="col-12">
          <li>api-url</li>
          <li>dataset-id ou dataset</li>
        </ul>
      </div>
      <!-- Dataset unavailable-->
      <div
        v-if="!['available', 'bad-configured'].includes(status)"
        class="q-pa-sm column-all-center border-secondary text-black"
      >
        <div class="row-all-center">
          <QIcon
            name="mdi-numeric-4-box-multiple-outline"
            size="md"
            class="q-mr-xs"
            color="secondary"
          /><QIcon
            name="mdi-numeric-0-box-multiple-outline"
            size="md"
            class="q-mr-xs"
            color="secondary"
          /><QIcon
            name="mdi-numeric-4-box-multiple-outline"
            size="md"
            class="q-mr-xs"
            color="secondary"
          />
        </div>
        <div class="row-all-center q-mt-md">
          Impossible d'afficher le jeu de données
          <QIcon
            name="mdi-sync"
            size="xs"
            class="on-right cursor-pointer"
            color="secondary"
            title="Réessayer de charger les données"
            @click="getDataset"
          />
        </div>
      </div>
      <!-- Dataset is here as a card -->
      <QCard
        v-if="status == 'available'"
        :tag="tagQCard"
        :href="hrefQCard"
        :title="titleQCard"
        bordered
        square
        class="dido-main-object"
        @[hasClickEventQCard].prevent="clickQCard"
      >
        <!-- Title and Logo -->
        <div class="row-start-center no-wrap q-col-gutter-md">
          <!-- Logo -->
          <span class="col-auto self-start">
            <QAvatar square size="32px">
              <img :src="logo" :title="`Thème '${datasetObject.topic}'`" />
            </QAvatar>
          </span>
          <!-- Title -->
          <span class="dido-card-title">{{ datasetObject.title }}</span>
        </div>

        <!-- Timestamps -->
        <div
          class="q-mt-sm row-start-center q-col-gutter-sm dido-card-timestamps"
        >
          <!-- Modification Date -->
          <span class="col-auto"> modifié le {{ modificationDate }} </span>
        </div>

        <!-- Description -->
        <div class="q-mt-md row-start-center">
          <DidoTextTruncate
            :text="datasetObject.description"
            :length="200"
            class="text-justify"
            :link-classes="[
              'cursor-pointer',
              'on-right',
              'text-primary',
              'text-underline',
              'text-small',
            ]"
          />
        </div>

        <!-- Resources -->
        <div
          class="q-mt-md row-start-center q-col-gutter-xs dido-dataset-card-resources"
        >
          <!-- Datafiles -->
          <div v-if="datafilesText" class="col-12">
            {{ datafilesText }}
          </div>
          <!-- Attachments -->
          <div v-if="attachmentsText" class="col-12">
            {{ attachmentsText }}
          </div>
        </div>

        <!-- Tags -->
        <div v-if="tags && tags.length" class="q-mt-md row-start-center">
          <DidoChipsList
            type="tag"
            from-dataset-or-datafile="dataset"
            :items="tags"
            size="sm"
            bg-color="secondary"
            text-color="black"
            @label-selection="($event) => emit('label-selection', $event)"
          />
        </div>

        <!-- Geographical coverage -->
        <div v-if="zones && zones.length" class="q-mt-md row-start-center">
          <DidoChipsList
            type="zone"
            from-dataset-or-datafile="dataset"
            :items="zones"
            size="sm"
            bg-color="secondary"
            text-color="black"
            icon="mdi-map-marker"
            @label-selection="($event) => emit('label-selection', $event)"
          />
        </div>

        <!-- Consultations counter -->
        <div
          v-if="consultationsCounterText"
          class="q-mt-md row-start-center dido-card-counter"
        >
          <span>{{ consultationsCounterText }}</span>
        </div>
      </QCard>
    </template>

    <!-- spinner during interaction with api -->
    <QInnerLoading :showing="loading">
      <QSpinner size="50px" color="secondary" />
      <div class="text-h6 text-secondary">Chargement...</div>
    </QInnerLoading>
  </div>
</template>

<style lang="sass" scoped>
.dido-widget-dataset-card
  min-height: 200px
</style>
